import { Box, Icon, Image } from '@zillow/constellation-10';
import { IconFileFilled, IconPhotosFilled, IconVideoCameraFilled } from '@zillow/constellation-icons';

type AttachmentIconProps = {
  isImageFileType: boolean;
  isVideoFileType: boolean;
  isDisabled: boolean;
  imageSrc?: string;
  /** Pass "" where the thumbnail sits beside the file name, so it doesn't prefix the surrounding control's name. */
  alt?: string;
};

const ATTACHMENT_ICON_SIZE = 48;
const DEFAULT_IMAGE_ALT = 'Image attachment thumbnail';

// Helper component for icon wrapper
function IconWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Box
      css={{
        backgroundColor: 'bg.softest',
        borderRadius: '300',
        minHeight: `${ATTACHMENT_ICON_SIZE}px`,
        minWidth: `${ATTACHMENT_ICON_SIZE}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size="md">{children}</Icon>
    </Box>
  );
}

export default function AttachmentIcon({
  isImageFileType,
  isVideoFileType,
  isDisabled,
  imageSrc,
  alt = DEFAULT_IMAGE_ALT,
}: AttachmentIconProps) {
  const shouldShowImageAttachment = isImageFileType && !isDisabled && imageSrc;

  // Image attachment with preview
  if (shouldShowImageAttachment) {
    return (
      <Image
        data-testid="image-thumbnail"
        src={imageSrc}
        alt={alt}
        width={ATTACHMENT_ICON_SIZE}
        height={ATTACHMENT_ICON_SIZE}
        css={{
          borderRadius: '300',
          overflow: 'hidden',
          width: `${ATTACHMENT_ICON_SIZE}px`,
          height: `${ATTACHMENT_ICON_SIZE}px`,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    );
  }

  // Disabled or missing image
  if (isImageFileType) {
    return (
      <IconWrapper>
        <IconPhotosFilled data-testid="icon-photos" />
      </IconWrapper>
    );
  }

  // Video attachment
  if (isVideoFileType) {
    return (
      <IconWrapper>
        <IconVideoCameraFilled data-testid="icon-video" />
      </IconWrapper>
    );
  }

  // Generic file
  return (
    <IconWrapper>
      <IconFileFilled data-testid="icon-file" />
    </IconWrapper>
  );
}
